import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useI18n } from "../../../hooks/i18n-hooks";
import { StyleSheet, View } from "react-native";
import VStack from "../../../ui_atoms/VStack";
import {
    calculatePayrollBreakdownMaxWorkDays,
    FormData,
    PayrollBreakdownData,
    PayrollBreakdownEditorProps,
    useValidateNumber,
} from "./PayrollBreakdownEditor";
import Gap from "../../../ui_atoms/Gap";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import TextField from "../../TextField";
import { BodyMediumText, BodySmallText, LinkSmallText, TitleMediumText } from "../../FormattedText";
import AbsenceCreateButton from "./AbsenceCreateButton";
import { Repetition } from "../../../__generated__/graphql";
import AbsenceList from "./AbsenceList";
import PayrollBreakdownCommon from "./PayrollBreakdownCommon";
import PayrollBreakdownHourlyInfo from "./PayrollBreakdownHourlyInfo";
import PayrollBreakdownTotalInfo from "./PayrollBreakdownTotalInfo";
import PayrollBreakdownAbsences from "./PayrollBreakdownAbsenses";
import DropdownField from "../../DropdownField";
import { useTheme } from "../../../hooks/use-theme";
import { SHORT_DATE, useDateFormatter } from "../../../hooks/date";
import { DynamicFees } from "./DynamicFees";
import FullWidthLine from "../../FullWidthLine";
import { useDeepCompareEffect } from "../../../hooks/use-deep-compare-effect";
import { useChainInputFocus } from "../../../hooks/use-chain-input-focus";
import PayrollBreakdownBonusEffectiveFeesOvertimePaid from "./PayrollBreakdownBonusEffectiveFeesOvertimePaid";
import { useIntercom } from "../../../hooks/intercom";

const displayDefaultHoursValue = (x?: number | undefined | null): string => {
    if (x === undefined || x === null) {
        return "";
    }
    const hours = x / 60;
    if (hours % 1 === 0) {
        return hours.toString();
    } else {
        return hours.toFixed(2);
    }
};

const displayDefaultNumberValue = (x?: string | undefined | null): string => {
    if (x === undefined || x === null) {
        return "";
    }
    return `${x}`;
};

const PayrollBreakdownEditorHourly: React.FC<PayrollBreakdownEditorProps> = ({
    payrollBreakdown,
    mission,
    vacationStatus,
    onValidData,
    onChange,
    needWorkDays,
    isFromTimesheet,
    timeSheetsNeedCompletion,
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();
    const validateNumber = useValidateNumber();
    const formatDate = useDateFormatter();
    const { presentArticleId } = useIntercom();
    const hoursPerDay = useMemo(() => {
        return parseFloat(vacationStatus?.contractHoursPerWeekFullTime || "0") / 5;
    }, [vacationStatus]);

    const {
        control,
        watch,
        formState: { isValid },
        resetField,
    } = useForm<FormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            totalWork100: displayDefaultHoursValue(payrollBreakdown.totalWork100),
            totalWork125: displayDefaultHoursValue(payrollBreakdown.totalWork125) || "0",
            totalWork150: displayDefaultHoursValue(payrollBreakdown.totalWork150) || "0",
            totalWorkedDays: displayDefaultNumberValue(payrollBreakdown.totalWorkedDays),
            absences: payrollBreakdown.absences || [],
            dynamicFees: payrollBreakdown.dynamicFees || [],
            effectiveFees: payrollBreakdown.effectiveFees || [],
            bonuses: payrollBreakdown.bonuses || [],
        },
    });

    const totalWork100 = watch("totalWork100");
    const totalWork125 = watch("totalWork125");
    const totalWork150 = watch("totalWork150");
    const absences = watch("absences");
    const effectiveFees = watch("effectiveFees");
    const dynamicFees = watch("dynamicFees");
    const bonuses = watch("bonuses");

    const totalWorkedDays = watch("totalWorkedDays");

    // mission that has less than 2500 minutes (~5 days) of vacation is a mission that is bellow 20%.
    // in this case we display vacations in hours by default
    const [vacationInputType, setVacationInputType] = useState(
        vacationStatus.contractVacationMinutes < 2500 ? "hours" : "days",
    );
    const [usedVacationMinutes, setUsedVacationMinutes] = useState(
        payrollBreakdown.usedVacationMinutes,
    );
    const [usedVacationInputValue, setUsedVacationInputValue] = useState(
        (payrollBreakdown.usedVacationMinutes || 0).toFixed(1),
    );
    const availableVacationAmountMinutes = useMemo(() => {
        const availableAmount =
            Number(totalWork100 || 0) * vacationStatus.contractVacationIndemnity +
            Number(totalWork125 || 0) * vacationStatus.contractVacationIndemnity +
            Number(totalWork150 || 0) * vacationStatus.contractVacationIndemnity +
            vacationStatus.availableVacationAmount;

        return (availableAmount / vacationStatus.contractBaseSalary) * 60;
    }, [
        totalWork100,
        totalWork125,
        totalWork150,
        vacationStatus.availableVacationAmount,
        vacationStatus.contractBaseSalary,
        vacationStatus.contractVacationIndemnity,
    ]);

    const endOfMonthVacationTotal = useCallback(
        (inputType: string) => {
            const endOfMonthMinutes = availableVacationAmountMinutes - (usedVacationMinutes || 0);
            if (inputType === "days") {
                return Number((endOfMonthMinutes / hoursPerDay / 60).toFixed(1));
            } else {
                return Number((endOfMonthMinutes / 60).toFixed(1));
            }
        },
        [availableVacationAmountMinutes, usedVacationMinutes, hoursPerDay],
    );

    const availableVacationAmount = useCallback(
        (inputType: string) => {
            if (inputType === "days") {
                return Number((availableVacationAmountMinutes / hoursPerDay / 60).toFixed(1));
            } else {
                return Number((availableVacationAmountMinutes / 60).toFixed(1));
            }
        },
        [availableVacationAmountMinutes, hoursPerDay],
    );
    useDeepCompareEffect(() => {
        const data: PayrollBreakdownData = isFromTimesheet
            ? {
                  usedVacationMinutes: undefined,
                  usedVacationAmount: undefined,
                  totalWork100: undefined,
                  totalWork125: undefined,
                  totalWork150: undefined,
                  totalWorkedDays: undefined,
                  absences: undefined,
                  dynamicFees: dynamicFees.map((f) => ({ refId: f.fee.id, count: f.count || 0 })),
                  bonuses: bonuses,
                  effectiveFees: effectiveFees,
              }
            : {
                  usedVacationMinutes:
                      usedVacationMinutes === null ? undefined : usedVacationMinutes,
                  usedVacationAmount: Math.round(
                      ((usedVacationMinutes || 0) / 60) * vacationStatus.contractBaseSalary,
                  ),
                  totalWork100: totalWork100 ? Math.round(Number(totalWork100) * 60) : undefined,
                  totalWork125: totalWork125 ? Math.round(Number(totalWork125) * 60) : undefined,
                  totalWork150: totalWork150 ? Math.round(Number(totalWork150) * 60) : undefined,
                  totalWorkedDays: needWorkDays ? Math.round(Number(totalWorkedDays)) : undefined,
                  absences: absences,
                  dynamicFees: dynamicFees.map((f) => ({ refId: f.fee.id, count: f.count || 0 })),
                  bonuses: bonuses,
                  effectiveFees: effectiveFees,
              };
        onChange(data);
        if (isValid) {
            if (isFromTimesheet) {
                onValidData(data);
            } else if (
                usedVacationMinutes !== undefined &&
                usedVacationMinutes !== null &&
                !hasOverflow
            ) {
                // usedVacationMinutes is not managed
                // by the form so we need to check also that it has been set.
                onValidData(data);
            } else {
                // If usedVacationMinutes is not set, form is not valid
                onValidData(null);
            }
        } else {
            onValidData(null);
        }
    }, [
        isValid,
        isFromTimesheet,
        totalWorkedDays,
        onValidData,
        needWorkDays,
        totalWork100,
        totalWork125,
        totalWork150,
        usedVacationMinutes,
        vacationStatus.contractBaseSalary,
        bonuses,
        effectiveFees,
        absences,
        dynamicFees,
    ]);

    type TotalWorkFields = Pick<FormData, "totalWork100" | "totalWork125" | "totalWork150">;
    const totalWorkFields = ["totalWork100", "totalWork125", "totalWork150"] as Array<
        keyof TotalWorkFields
    >;

    const { append: appendAbsence, remove: removeAbsenceAtIndex } = useFieldArray({
        name: "absences",
        control,
    });

    const {
        append: appendEffectiveFee,
        remove: removeEffectiveFeeAtIndex,
        update: updateEffectiveFeeAtIndex,
    } = useFieldArray({
        name: "effectiveFees",
        control,
    });

    const {
        append: appendBonus,
        remove: removeBonusAtIndex,
        update: updateBonusAtIndex,
    } = useFieldArray({
        name: "bonuses",
        control,
    });

    const { chainInputFocus } = useChainInputFocus();

    const showVacationInfo = useMemo(() => {
        const repetition = mission?.currentContract?.workingTimeClassicSpec?.repetition;
        return repetition && repetition !== Repetition.None;
    }, [mission]);

    useEffect(() => {
        // if filled by timesheet we do not need to compute the minutes from the input value
        if (!isFromTimesheet) {
            const parsedValue = parseFloat(usedVacationInputValue || "");
            if (isNaN(parsedValue)) {
                setUsedVacationMinutes(undefined);
            } else if (vacationInputType === "days") {
                setUsedVacationMinutes(Math.round(parsedValue * 60 * hoursPerDay));
            } else {
                setUsedVacationMinutes(Math.round(parsedValue * 60));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usedVacationInputValue, hoursPerDay, setUsedVacationMinutes, isFromTimesheet]);

    useEffect(() => {
        setUsedVacationInputValue("0");
    }, [vacationInputType, setUsedVacationInputValue]);

    const payrollBreakdownBonusEffectiveFeesOvertimePaid = useMemo(
        () => (
            <PayrollBreakdownBonusEffectiveFeesOvertimePaid
                control={control}
                resetField={resetField}
                bonuses={{
                    clientDeductions: mission?.currentContract?.clientDeductions || [],
                    onCreate: appendBonus,
                    onUpdate: updateBonusAtIndex,
                    onDeleteAtIndex: removeBonusAtIndex,
                }}
                effectiveFees={{
                    onCreate: appendEffectiveFee,
                    onUpdate: updateEffectiveFeeAtIndex,
                    onDeleteAtIndex: removeEffectiveFeeAtIndex,
                }}
            />
        ),
        [
            appendEffectiveFee,
            appendBonus,
            removeBonusAtIndex,
            updateBonusAtIndex,
            control,
            mission?.currentContract?.clientDeductions,
            removeEffectiveFeeAtIndex,
            resetField,
            updateEffectiveFeeAtIndex,
        ],
    );

    const hasOverflow = useMemo(() => {
        return endOfMonthVacationTotal(vacationInputType) < 0;
    }, [endOfMonthVacationTotal, vacationInputType]);

    const maxWorkDays = useMemo(() => {
        if (!mission?.currentContract?.startDate || !payrollBreakdown) {
            return 0;
        }
        return calculatePayrollBreakdownMaxWorkDays({
            contractStart: mission.currentContract.startDate,
            contractEnd: mission.currentContract.endDate,
            payrollBreakdownStart: payrollBreakdown.startDate,
            payrollBreakdownEnd: payrollBreakdown.endDate,
        });
    }, [mission?.currentContract?.endDate, mission?.currentContract?.startDate, payrollBreakdown]);

    return (
        <VStack>
            {isFromTimesheet ? (
                <>
                    <VStack h="2/3">
                        <PayrollBreakdownHourlyInfo
                            payrollBreakdown={payrollBreakdown}
                            disabled={timeSheetsNeedCompletion}
                        />
                        <Gap gap={0.25} />
                        <PayrollBreakdownTotalInfo
                            payrollBreakdown={payrollBreakdown}
                            vacationStatus={vacationStatus}
                            disabled={timeSheetsNeedCompletion}
                        />
                        <Gap gap={1} />
                        <FullWidthLine />
                        <DynamicFees
                            control={control}
                            dynamicFees={dynamicFees}
                            clientDeductions={mission?.currentContract?.clientDeductions || []}
                            chainInputFocus={chainInputFocus}
                        />
                        <Gap gap={1} />
                        {payrollBreakdownBonusEffectiveFeesOvertimePaid}
                    </VStack>
                    <PayrollBreakdownAbsences absences={payrollBreakdown.absences} />
                </>
            ) : (
                <VStack h="2/3">
                    <BodyMediumText>
                        {i18n.t("payroll_breakdown.worked_hours.title")}
                    </BodyMediumText>
                    <Gap gap={1} />
                    {totalWorkFields.map((k, idx) => (
                        <Controller
                            key={k}
                            name={k}
                            control={control}
                            rules={{
                                validate: {
                                    isNumber: validateNumber,
                                },
                            }}
                            render={({
                                field: { onChange, value, onBlur },
                                fieldState: { error },
                            }) => (
                                <TextField
                                    {...chainInputFocus(idx)}
                                    isSelfContained
                                    label={i18n.t(`payroll_breakdown.field_${k}`)}
                                    value={value}
                                    onChangeText={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    selectTextOnFocus
                                    showErrorText
                                    keyboardType="numeric"
                                    isDecimalNumber
                                    style={[
                                        styles.stackedField,
                                        idx !== 0 ? styles.stackedFieldNoTop : null,
                                        idx !== totalWorkFields.length - 1
                                            ? styles.stackedFieldNoBottom
                                            : null,
                                        { alignSelf: "flex-start" },
                                    ]}
                                    inputStyle={{
                                        textAlign: "right",
                                    }}
                                />
                            )}
                        />
                    ))}
                    <Gap gap="1/3" />
                    <BodySmallText color={colors.greyAccessible}>
                        {i18n.t("payroll_breakdown.worked_hours.description")}
                    </BodySmallText>
                    <LinkSmallText
                        onPress={() => {
                            void presentArticleId("10485414");
                        }}
                    >
                        {i18n.t("payroll_breakdown.worked_hours.link")}
                    </LinkSmallText>
                    <Gap gap={1} />
                    <FullWidthLine />
                    <Gap gap={1} />
                    <PayrollBreakdownCommon
                        fees={mission?.currentContract?.fees || []}
                        deductions={mission?.currentContract?.deductions || []}
                        needWorkDays={needWorkDays}
                        control={control}
                        chainInputFocus={(index) => chainInputFocus(index + 3)}
                        maxWorkDays={maxWorkDays}
                    />
                    {showVacationInfo && (
                        <>
                            <Gap gap={1} />
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.field_total_vacation_days_title")}
                            </BodyMediumText>
                            <Gap gap={0.5} />
                            <View style={{ flexDirection: "row", alignItems: "center" }}>
                                <TextField
                                    {...chainInputFocus(4)}
                                    value={usedVacationInputValue}
                                    onChangeText={setUsedVacationInputValue}
                                    showErrorText={false}
                                    isDecimalNumber
                                    keyboardType="numeric"
                                    selectTextOnFocus
                                    returnKeyType="done"
                                    style={{ alignSelf: "flex-start", minWidth: "50%" }}
                                    error={hasOverflow}
                                    inputStyle={{
                                        textAlign: "right",
                                        width: undefined,
                                        minWidth: 0,
                                    }}
                                />
                                <Gap gap={1 / 2} />
                                <DropdownField<string>
                                    numberOfLines={1}
                                    style={{ flexShrink: 1, flexGrow: 1, flexBasis: "75%" }}
                                    items={[
                                        {
                                            label: i18n.t("payroll_breakdown.vacancy_by_days"),
                                            value: "days",
                                        },
                                        {
                                            label: i18n.t("payroll_breakdown.vacancy_by_hours"),
                                            value: "hours",
                                        },
                                    ]}
                                    onSelect={setVacationInputType}
                                    value={vacationInputType}
                                />
                            </View>
                            <Gap gap={1 / 3} />
                            {hasOverflow ? (
                                <BodySmallText color={colors.error}>
                                    {i18n.t(
                                        vacationInputType === "days"
                                            ? "payroll_breakdown.used_vacation_days_overflow"
                                            : "payroll_breakdown.used_vacation_hours_overflow",
                                        {
                                            count: availableVacationAmount(vacationInputType),
                                        },
                                    )}
                                </BodySmallText>
                            ) : (
                                <BodySmallText color={colors.greyAccessible}>
                                    {i18n.t(
                                        vacationInputType === "days"
                                            ? "payroll_breakdown.used_vacation_days_info"
                                            : "payroll_breakdown.used_vacation_hours_info",
                                        {
                                            count: endOfMonthVacationTotal(vacationInputType),
                                            date: formatDate(
                                                new Date(payrollBreakdown.endDate),
                                                SHORT_DATE,
                                            ),
                                        },
                                    )}
                                </BodySmallText>
                            )}
                            <Gap gap="2/3" />
                            <FullWidthLine />
                        </>
                    )}
                    <DynamicFees
                        control={control}
                        dynamicFees={dynamicFees}
                        clientDeductions={mission?.currentContract?.clientDeductions || []}
                        chainInputFocus={(index) => chainInputFocus(index + 5)}
                    />
                    <Gap gap={1} />
                    <VStack gap="1/3">
                        <TitleMediumText>{i18n.t("absence.title")}</TitleMediumText>
                        <>
                            <AbsenceList
                                absences={absences}
                                onDeleteAbsenceAtIndex={removeAbsenceAtIndex}
                            />
                            <AbsenceCreateButton
                                onCreate={appendAbsence}
                                minDate={
                                    payrollBreakdown.startDate
                                        ? new Date(payrollBreakdown.startDate)
                                        : undefined
                                }
                                maxDate={
                                    payrollBreakdown.endDate
                                        ? new Date(payrollBreakdown.endDate)
                                        : undefined
                                }
                            />
                        </>
                    </VStack>
                    <Gap gap={1} />
                    {payrollBreakdownBonusEffectiveFeesOvertimePaid}
                </VStack>
            )}
            <Gap gap={2} />
        </VStack>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                stackedField: {
                    marginTop: -2,
                },
                stackedFieldNoTop: {
                    borderTopRightRadius: 0,
                    borderTopLeftRadius: 0,
                },
                stackedFieldNoBottom: {
                    borderBottomRightRadius: 0,
                    borderBottomLeftRadius: 0,
                },
            }),
        [],
    );
};

export default PayrollBreakdownEditorHourly;
